import React, { useEffect } from "react";
import Logo from "../assets/logo.svg";
import LogoDark from "../assets/LogoDark.svg";
import { toast } from "react-hot-toast";
import { signIn } from "../controllers/auth.controller";
import { Link, useNavigate } from "react-router-dom";
import { isRestroUserAuthenticated } from "../helpers/AuthStatus";
import {
  getUserDetailsInLocalStorage,
  saveUserDetailsInLocalStorage,
} from "../helpers/UserDetails";
import { SCOPES } from "../config/scopes";
import { useTranslation } from "react-i18next";
import { useTheme } from "../contexts/ThemeContext";
import socket, { initSocket } from "../utils/socket";


export default function LoginPage() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();

  /* ------------------------------------------------------------------ */
  /* 1. Auto-redirect if already logged in                              */
  /* ------------------------------------------------------------------ */
 // KEEP

useEffect(() => {
  if (isRestroUserAuthenticated()) {
    const user = getUserDetailsInLocalStorage();

    if (user.role === "superadmin") {
      navigate("/superadmin/dashboard/home", { replace: true });
    } else if (user.role === "admin") {
      navigate("/dashboard/home", { replace: true });
    } else {
      const scopes = user.scope ? user.scope.split(",").map(s => s.trim()) : [];
      if (scopes.includes(SCOPES.DASHBOARD)) {
        navigate("/dashboard/home", { replace: true });
      } else if (scopes.includes(SCOPES.POS)) {
        navigate("/dashboard/pos", { replace: true });
      } else if (scopes.includes(SCOPES.ORDERS)) {
        navigate("/dashboard/orders", { replace: true });
      } else if (scopes.includes(SCOPES.ROOMS) || scopes.includes(SCOPES.PMS)) {
        navigate("/dashboard/pms/rooms", { replace: true });
      } else if (scopes.includes(SCOPES.HOUSEKEEPING)) {
        navigate("/dashboard/pms/housekeeping", { replace: true });
      } else if (scopes.includes(SCOPES.LAUNDRY)) {
        navigate("/dashboard/pms/laundry", { replace: true });
      } else if (scopes.includes(SCOPES.KITCHEN) || scopes.includes(SCOPES.KITCHEN_DISPLAY)) {
        navigate("/dashboard/kitchen", { replace: true });
      } else if (scopes.includes(SCOPES.INVENTORY) || scopes.includes(SCOPES.VIEW_INVENTORY)) {
        navigate("/dashboard/inventory", { replace: true });
      } else if (scopes.includes(SCOPES.INVOICES)) {
        navigate("/dashboard/invoices", { replace: true });
      } else if (scopes.includes(SCOPES.EXPENSES)) {
        navigate("/dashboard/expenses", { replace: true });
      } else if (scopes.includes(SCOPES.CUSTOMERS) || scopes.includes(SCOPES.VIEW_CUSTOMERS)) {
        navigate("/dashboard/customers", { replace: true });
      } else if (scopes.includes(SCOPES.RESERVATIONS) || scopes.includes(SCOPES.VIEW_RESERVATIONS)) {
        navigate("/dashboard/reservation", { replace: true });
      } else {
        navigate("/dashboard/profile", { replace: true });
      }
    }
  }
}, [navigate]);

  /* ------------------------------------------------------------------ */
  /* 2. Handle login form submission                                    */
  /* ------------------------------------------------------------------ */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const username = e.target.username.value.trim();
    const password = e.target.password.value;

    if (!username) {
      e.target.username.focus();
      toast.error(t("login.username_error"));
      return;
    }
    if (!password) {
      e.target.password.focus();
      toast.error(t("login.password_error"));
      return;
    }

    const loadingId = toast.loading(t("login.loading_message"));

    try {
      const res = await signIn(username, password);
      toast.dismiss(loadingId);

      if (res.status !== 200) {
        toast.error(res.data?.message || t("login.error_message"));
        return;
      }

      const { accessToken, user } = res.data;
      const userWithToken = { ...user, token: accessToken };

      // Save user
      saveUserDetailsInLocalStorage(userWithToken);

      // Initialize socket and authenticate
      initSocket();
      if (user?.tenant_id) {
        socket.emit("authenticate", user.tenant_id);
      }
      window.dispatchEvent(new Event("auth_state_changed"));

      // Send auth token to Electron main process
      try {
        let ipc = null;
        if (window.electron && window.electron.ipcRenderer) {
          ipc = window.electron.ipcRenderer;
        } else if (typeof window.require === 'function') {
          ipc = window.require('electron').ipcRenderer;
        }
        if (ipc && accessToken) {
          ipc.send('set-auth-token', accessToken);
        }
      } catch (e) {}

      toast.success(t("login.success_message"));

      // Redirect
      if (user.role === "superadmin") {
        navigate("/superadmin/dashboard/home", { replace: true });
      } else if (user.role === "admin") {
        navigate("/dashboard/home", { replace: true });
      } else {
        const scopes = user.scope ? user.scope.split(",").map(s => s.trim()) : [];
        if (scopes.includes(SCOPES.DASHBOARD)) {
          navigate("/dashboard/home", { replace: true });
        } else if (scopes.includes(SCOPES.POS)) {
          navigate("/dashboard/pos", { replace: true });
        } else if (scopes.includes(SCOPES.ORDERS)) {
          navigate("/dashboard/orders", { replace: true });
        } else if (scopes.includes(SCOPES.ROOMS) || scopes.includes(SCOPES.PMS)) {
          navigate("/dashboard/pms/rooms", { replace: true });
        } else if (scopes.includes(SCOPES.HOUSEKEEPING)) {
          navigate("/dashboard/pms/housekeeping", { replace: true });
        } else if (scopes.includes(SCOPES.LAUNDRY)) {
          navigate("/dashboard/pms/laundry", { replace: true });
        } else if (scopes.includes(SCOPES.KITCHEN) || scopes.includes(SCOPES.KITCHEN_DISPLAY)) {
          navigate("/dashboard/kitchen", { replace: true });
        } else if (scopes.includes(SCOPES.INVENTORY) || scopes.includes(SCOPES.VIEW_INVENTORY)) {
          navigate("/dashboard/inventory", { replace: true });
        } else if (scopes.includes(SCOPES.INVOICES)) {
          navigate("/dashboard/invoices", { replace: true });
        } else if (scopes.includes(SCOPES.EXPENSES)) {
          navigate("/dashboard/expenses", { replace: true });
        } else if (scopes.includes(SCOPES.CUSTOMERS) || scopes.includes(SCOPES.VIEW_CUSTOMERS)) {
          navigate("/dashboard/customers", { replace: true });
        } else if (scopes.includes(SCOPES.RESERVATIONS) || scopes.includes(SCOPES.VIEW_RESERVATIONS)) {
          navigate("/dashboard/reservation", { replace: true });
        } else {
          navigate("/dashboard/profile", { replace: true });
        }
      }
    } catch (err) {
      toast.dismiss(loadingId);
      const msg = err?.response?.data?.message || t("login.error_message");
      toast.error(msg);
    }
  };

  // ... rest of UI (unchanged)

  /* ------------------------------------------------------------------ */
  /* 3. UI – Login Form                                                 */
  /* ------------------------------------------------------------------ */
  return (
    <div className="relative overflow-x-hidden md:overflow-hidden bg-restro-green-light dark:bg-restro-card-bg">
      <img
        src="/assets/circle_illustration.svg"
        alt="illustration"
        className="absolute w-96 lg:w-[1024px] h-96 lg:h-[1024px] lg:-bottom-96 lg:-right-52 -right-36 opacity-80"
      />

      <div className="flex flex-col md:flex-row items-center justify-end md:justify-between gap-10 h-screen container mx-auto px-4 md:px-0 py-4 md:py-0 relative lg:px-12">
        {/* Left Text */}
        <div className="lg:mx-12">
          <h3 className="text-2xl lg:text-6xl font-black text-restro-green-dark dark:text-restro-green-dark-mode">
            {t("home.cafe_restaurant")}
          </h3>
          <h3 className="text-2xl lg:text-6xl font-black outline-text text-restro-green-light dark:text-restro-green">
            {t("home.hotel_bar")}
          </h3>
        </div>

        {/* Login Card */}
        <div className="sm:w-96 mx-8 sm:mx-0 shadow-2xl lg:mx-12 rounded-2xl px-8 py-8 w-full bg-white dark:bg-black border border-restro-green-light">
          <div className="flex items-center justify-between">
            <div className="text-xl font-medium text-restro-text dark:text-white">
              {t("login.title")}
            </div>
            <div>
              <img
                src={theme === "black" ? LogoDark : Logo}
                className="h-16"
                alt="AsRestro Logo"
              />
            </div>
          </div>

          <form className="mt-6" onSubmit={handleSubmit}>
            {/* Email */}
            <div>
              <label htmlFor="username" className="text-restro-text dark:text-white">
                {t("login.email_label")}
              </label>
              <input
                type="email"
                id="username"
                name="username"
                required
                placeholder={t("login.email_placeholder")}
                className="mt-1 block w-full px-4 py-3 rounded-xl outline-none focus-visible:ring-1 bg-restro-gray text-restro-text focus-visible:ring-restro-ring dark:bg-gray-800 dark:text-white"
                tabIndex={1}
              />
            </div>

            {/* Password */}
            <div className="mt-4">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="text-restro-text dark:text-white">
                  {t("login.password_label")}
                </label>
                <Link
                  tabIndex={5}
                  className="block text-xs text-restro-text hover:underline"
                  to="/forgot-password"
                >
                  {t("login.forgot_password")}
                </Link>
              </div>
              <input
                type="password"
                id="password"
                name="password"
                required
                placeholder={t("login.password_placeholder")}
                className="mt-1 block w-full px-4 py-3 rounded-xl outline-none focus-visible:ring-1 bg-restro-gray text-restro-text focus-visible:ring-restro-ring dark:bg-gray-800 dark:text-white"
                tabIndex={2}
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="block w-full mt-6 text-white rounded-xl px-4 py-3 transition hover:scale-105 active:scale-95 hover:shadow-md outline-none focus-visible:ring-1 bg-restro-green focus-visible:ring-restro-ring hover:bg-restro-green-button-hover font-medium"
              tabIndex={3}
            >
              {t("login.login_button")}
            </button>

            {/* Divider */}
            <div className="flex items-center gap-4 my-4">
              <div className="flex-1 border-b border-restro-gray" />
              <p className="text-sm text-gray-400">{t("login.or")}</p>
              <div className="flex-1 border-b border-restro-gray" />
            </div>

            {/* Register */}
            <Link
              to="/register"
              className="block w-full text-center rounded-xl px-4 py-3 transition hover:scale-105 active:scale-95 hover:shadow-xl outline-none focus-visible:ring-1 bg-restro-gray hover:bg-restro-button-hover focus-visible:ring-restro-ring dark:focus-visible:ring-restro-ring-dark font-medium"
              tabIndex={4}
            >
              {t("login.create_account")}
            </Link>
          </form>
        </div>
      </div>
    </div>
  );
}